"use client"
import { withMediaUrl } from "@/app/lib/basePath";
import { fadeUp, stagger } from "@/app/lib/utils/motionVariants";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/app/ui/accordion";
import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import CTASection from "../home/CTA/CTASection";
import { defaultAIProvidersConfig } from "@/app/lib/config/aiProviders";
import AIProviders from "../ui/AIProviders";


export default function FeaturePageDetail(data: any) {
    const postData = data.data;
    const faqs = data.data.content.filter((item: any) => item.type === "faq");
    const title = postData.title ?? "";
    const highlight = postData.titleHighlight?.trim();
    const highlightIndex = highlight
        ? title.toLowerCase().indexOf(highlight.toLowerCase())
        : -1;
    return (
        <>
            <section className="relative custom-minheight flex flex-col items-center justify-center">
                <div className="-translate-x-1/2 -z-1 absolute aspect-square left-1/2 max-w-full lg:max-w-[2560px] md:aspect-auto md:min-h-[1000px] pointer-events-none top-0 w-full">
                    <div className='shadow-[0_0_120px_110px_rgb(36,91,187),0_0_220px_218px_rgb(36,91,187),0_0_790px_320px_rgb(36,91,187)] h-[0.1px] w-[0.1px] left-1/3 top-1/4 -translate-middle absolute rounded-full backdrop-blur-xl bg-[rgb(22,154,156)] scale-150 opacity-40'></div>
                    <div className="absolute -bottom-40 left-0 w-full md:w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
                    <div className="absolute -bottom-40 right-0 w-full md:w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full lg:w-[800px] h-[800px] bg-accent/10 rounded-full blur-3xl" />
                </div>

                <div className="container mx-auto relative z-10 pt-24 lg:pt-32 pb-4 md:pb-6 lg:pb-8">
                    <motion.div initial="hidden" animate="visible" variants={stagger}>
                        {/* <span className="inline-block px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white/80 text-sm mb-4">{postData.category.name}</span> */}
                        <motion.h1
                            variants={fadeUp}
                            className="banner-title max-w-4xl mx-auto text-center"
                        >
                            {highlightIndex >= 0 && highlight ? (
                                <>
                                    {title.slice(0, highlightIndex)}
                                    <span className="banner-highlight-title">
                                        {title.slice(highlightIndex, highlightIndex + highlight.length)}
                                    </span>
                                    {title.slice(highlightIndex + highlight.length)}
                                </>
                            ) : (
                                title
                            )}
                        </motion.h1>
                        <div className="flex items-center gap-3 mt-3 lg:mt-8 justify-center">
                            {/* <motion.div className="w-10 h-10 md:w-12 md:h-12 rounded-full overflow-hidden border border-white/10 shrink-0">
                                <Image
                                    src="https://naavik.ai/favicon.svg"
                                    alt="Author"
                                    className="w-full h-full object-cover"
                                    title="Author"
                                    width={48}
                                    height={48}
                                    loading="lazy"
                                    fetchPriority="high"
                                    unoptimized
                                />
                            </motion.div> */}
                            <div>
                                {/* <motion.div className="text-white text-lg font-medium leading-none mb-2">
                                    {postData.author.name}
                                </motion.div> */}

                                <motion.div variants={fadeUp} className="flex items-center gap-2 banner-description !text-sm">
                                    <Calendar className="w-5 h-5" />
                                    {new Date(postData.publishAt).toLocaleDateString("en-GB", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                    })}
                                </motion.div>
                            </div>

                        </div>

                    </motion.div>
                    {postData.coverImage && (
                        <motion.div
                            initial={{ opacity: 0, y: 50 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="mt-6 lg:mt-10 max-w-4xl mx-auto"
                        >
                            <div className="relative">
                                <div aria-hidden className="absolute -inset-4 bg-gradient-to-r from-primary/30 to-secondary/30 rounded-3xl blur-2xl w-full md:w-auto" />
                                <motion.div className="bg-[#000c1e]/40 backdrop-blur-xl rounded-2xl flex items-center justify-center p-2 shadow-[0_0_50px_rgba(14,165,233,0.1),0_30px_80px_-20px_rgba(14,165,233,0.25)] border border-white/20">
                                    <Image
                                        src={withMediaUrl(postData.coverImage.url)}
                                        alt={postData.coverImage.alternativeText || "Feature Image"}
                                        title={postData.coverImage.alternativeText || "Feature Image"}
                                        className="object-cover w-full h-full rounded-[10px]"
                                        width={512}
                                        height={378}
                                        decoding="async"
                                        loading="lazy"
                                        fetchPriority="high"
                                    />
                                </motion.div>
                            </div>
                            <div className="mt-2 pt-2">
                                <AIProviders
                                    data={postData?.ai_configuration || defaultAIProvidersConfig}
                                    variant="inline"
                                    size="sm"
                                    showTitle={true}
                                    showLabelsOnMobile={false}
                                    className="float-right"
                                />
                            </div>
                        </motion.div>
                    )}
                     
                </div>
            </section>

            <section className="pb-4 md:pb-6 lg:pb-8 overflow-hidden">
                <div className="container mx-auto">
                    <div className="post-content mx-auto max-w-4xl ">

                        {postData?.content?.map((item: any, index: number) => (
                            <div key={`${item.type}-${index}`}>
                                {item.type === "text" && (
                                    <div
                                        className="prose prose-invert max-w-none"
                                        dangerouslySetInnerHTML={{
                                            __html: item.html,
                                        }}
                                    />
                                )}
                                {item.type === "image" && (
                                    <figure className="space-y-3">

                                        <div className="relative  overflow-hidden rounded-2xl">
{/* aspect-video */}
                                            <Image
                                                src={withMediaUrl(item.imageUrl)}
                                                alt={item.imageAlt || "Blog Image"}
                                                title={item.imageAlt || "Blog Image"}
                                                className="object-cover h-full w-full"
                                                width={900}
                                                height={574}
                                                loading="lazy"
                                                fetchPriority="high"
                                            />

                                        </div>

                                        {item.caption && (
                                            <figcaption className="text-center text-sm text-white/60">
                                                {item.caption}
                                            </figcaption>
                                        )}

                                    </figure>
                                )}
                            </div>
                        ))}

                         {faqs.length > 0 && (
                             <>
                            <h2 className="section-title mb-1 lg:mb-4 text-center mt-12">
                                Frequently Asked Questions
                            </h2>
                            <section className="relative py-6 lg:py-8">
                                <div aria-hidden className="absolute inset-0 star-field pointer-events-none" />
                                <div className="container mx-auto max-w-4xl relative z-10">
                                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
                                        <Accordion type="single" collapsible className="space-y-3">
                                            {faqs.map((faq: any, i: number) => (
                                                <motion.div key={i} variants={fadeUp}>
                                                    <AccordionItem
                                                        value={`item-${i}`}
                                                        className="group rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-md shadow-[0_4px_24px_-12px_rgba(0,0,0,0.4)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/[0.06] hover:border-white/20 hover:shadow-[0_8px_32px_-12px_hsl(var(--primary)/0.25)] data-[state=open]:bg-white/[0.07] data-[state=open]:border-white/25 px-5"
                                                    >
                                                        <AccordionTrigger className="hover:no-underline text-left font-semibold py-3.5 leading-normal ">{faq.question}</AccordionTrigger>
                                                        <AccordionContent className="text-muted-foreground leading-relaxed pb-5 -translate-y-1">
                                                            <div
                                                                className="post-content mx-auto max-w-4xl "
                                                                dangerouslySetInnerHTML={{ __html: faq.answerHtml }}
                                                            />
                                                        </AccordionContent>
                                                    </AccordionItem>
                                                </motion.div>
                                            ))}
                                        </Accordion>
                                    </motion.div>
                                </div>
                            </section>
                            </>
                        )}
                    </div>
                </div>
            </section>

            <section className="py-6 lg:py-8 relative z-[1]">
                <div className="container mx-auto">

                    <motion.div
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={stagger}
                        className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-8 mx-auto max-w-6xl"
                    >

                        {postData.recommendedBlogs?.map((post: any) => (
                            <motion.div
                                key={post.id}
                                variants={fadeUp}
                                className="flex flex-col"
                            >
                                <Link
                                    href={`/blogs/${post.slug}`}
                                    className="flex flex-col group flex-grow"
                                    title={post.title}
                                >

                                    <div className="overflow-hidden transition-all hover:-translate-y-1 bg-white/[0.04] backdrop-blur-[8px] border border-white/10 shadow-xl lg:border-r lg:border-white/10 rounded-lg flex-grow flex flex-col">

                                        <div className="aspect-video relative">

                                            <Image
                                                className="object-cover h-full w-full"
                                                src={withMediaUrl(post.coverImage?.url)}
                                                alt={post.title}
                                                title={post.title}
                                                width={360}
                                                height={202}
                                                loading="lazy"
                                                fetchPriority="high"
                                            />

                                            <span className="px-3 py-1.5 rounded-full text-xs border border-teal-400/30 text-white transition-all duration-[250ms] ease-out bg-teal-700/60 hover:border-teal-400/50 backdrop-blur-[8px] absolute top-2 right-2">
                                                {post.category.name}
                                            </span>

                                        </div>

                                        <div className="overflow-hidden group p-6 flex flex-col flex-grow">

                                            <h3 className="text-lg font-semibold group-hover:text-secondary transition-colors">
                                                {post.title}
                                            </h3>

                                            <p className="mt-2 text-white/70 text-sm leading-[1.7]">
                                                {post.excerpt}
                                            </p>

                                            <div className="flex items-center gap-3 justify-between mt-auto">

                                                <div className="mt-4 flex items-center gap-2 text-secondary font-semibold text-sm group-hover:gap-3 transition-all">
                                                    Read More
                                                    <ArrowRight className="w-4 h-4" />
                                                </div>

                                                <span className="text-xs text-white/70 flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {new Date(post.publishAt).toLocaleDateString("en-GB", {
                                                        day: "2-digit",
                                                        month: "short",
                                                        year: "numeric",
                                                    })}

                                                </span>

                                            </div>

                                        </div>

                                    </div>

                                </Link>
                            </motion.div>
                        ))}

                    </motion.div>

                </div>
            </section>

            <CTASection data={postData.transform_section} />
        </>
    );
}
